<script setup lang="ts">
import LeSelectorButton from "@dsk/components/SelectorButton/LeSelectorButton.vue";
import LeSelectorButtonGroup from "@dsk/components/SelectorButton/LeSelectorButtonGroup.vue";
import LeStar from "@dsk/icons/LeStar.vue";
import LeStarOutline from "@dsk/icons/LeStarOutline.vue";
import { range } from "lodash-es";
import { type Component, computed } from "vue";
import { useI18n } from "vue-i18n";

const rating = defineModel<number>({ default: 0 });

const { t } = useI18n();

interface Star {
  rating: number;
  component: Component;
}

const stars = computed<Star[]>(() =>
  range(1, 6).map((i) => {
    const isFilled = i <= rating.value;

    return {
      rating: i,
      component: isFilled ? LeStar : LeStarOutline
    };
  })
);

const onStarClick = (star: Star) => {
  rating.value = star.rating;
};
</script>

<template>
  <LeSelectorButtonGroup
    class="le-feedback-modal-stars"
    :selected="-1"
    :items="stars"
    @input="onStarClick($event.item)"
  >
    <template #default="{ state, handleBlur, handleFocusNext, handleFocusPrev, handleSelect }">
      <LeSelectorButton
        v-for="(star, index) in stars"
        :key="star.rating"
        :aria-label="t('le-feedback-modal-stars.button-aria-label', { n: star.rating }, star.rating)"
        :index="index"
        :tabbable="state.tabbableIndex === index"
        :selected="state.selectedIndex === index"
        class="le-feedback-modal-stars__star"
        @blur="handleBlur()"
        @select="handleSelect($event)"
        @focus-next="handleFocusNext($event)"
        @focus-prev="handleFocusPrev($event)"
      >
        <Component :is="star.component" width="24" height="24" />
      </LeSelectorButton>
    </template>
  </LeSelectorButtonGroup>
</template>

<style lang="less" scoped>
@import "@dsk/styles/all.less";

.le-feedback-modal-stars {
  &__star {
    // Unset styles from LeSelectorButton
    padding: 0 !important;
    border: none !important;
    background: none !important;
    color: @colors[@secondary][800] !important;
  }
}
</style>
